import { type FC } from 'react';

import { Checkbox, Pill } from '@/ui';

import type { Props } from './types';

const PrimaryProviderAssignmentPanel: FC<Props> = ({
  canSetPrimary,
  checked,
  currentRelationshipsCount,
  currentStaffNames,
  isSelectedProviderCurrent,
  onCheckedChange,
  relationshipType,
  selectedProviderName,
  tooManyPrimaries
}) => (
  <div className="mt-4 rounded-lg border border-uniform-gray-200 bg-uniform-gray-50 p-4 text-left dark:border-uniform-gray-700 dark:bg-uniform-gray-800">
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs font-semibold uppercase tracking-wide text-uniform-gray-500 dark:text-uniform-gray-400">
        {relationshipType.name}
      </span>
      {isSelectedProviderCurrent ? (
        <Pill color="secondary" variant="subtle" size="sm">
          Already primary
        </Pill>
      ) : (
        currentRelationshipsCount === 0 && (
          <Pill color="warning" variant="subtle" size="sm">
            None assigned
          </Pill>
        )
      )}
    </div>

    <div className="mt-3 grid grid-cols-2 items-start gap-3">
      <div className="min-w-0">
        <p className="text-xs text-uniform-gray-400">
          {currentRelationshipsCount > 1 ? `Current (${currentRelationshipsCount})` : 'Current'}
        </p>
        <p
          className={`truncate text-sm font-semibold ${
            currentRelationshipsCount
              ? 'text-uniform-gray-900 dark:text-uniform-gray-100'
              : 'text-uniform-gray-400'
          }`}>
          {currentStaffNames}
        </p>
      </div>
      <div className="min-w-0">
        <p className="text-xs text-uniform-gray-400">Selected</p>
        <p
          className={`truncate text-sm font-semibold ${
            canSetPrimary
              ? 'text-blue-700 dark:text-blue-400'
              : 'text-uniform-gray-900 dark:text-uniform-gray-100'
          }`}>
          {selectedProviderName}
        </p>
      </div>
    </div>

    {canSetPrimary && (
      <div className="mt-3.5 border-t border-uniform-gray-200 pt-3.5 dark:border-uniform-gray-700">
        <Checkbox
          id="set-primary-provider"
          checked={checked}
          onChange={onCheckedChange}
          label={
            currentRelationshipsCount
              ? `Make ${selectedProviderName} the ${relationshipType.name.toLowerCase()}`
              : `Set ${selectedProviderName} as the ${relationshipType.name.toLowerCase()}`
          }
          helpText={
            currentRelationshipsCount
              ? `Replaces ${currentStaffNames} on this patient's care team. Existing appointments are unchanged.`
              : `This patient has no ${relationshipType.name.toLowerCase()} yet.`
          }
        />
      </div>
    )}
    {tooManyPrimaries && (
      <div className="mt-3.5 border-t border-uniform-gray-200 pt-3.5 text-sm leading-relaxed text-uniform-gray-500 dark:border-uniform-gray-700 dark:text-uniform-gray-400">
        This patient has more than one {relationshipType.name.toLowerCase()}, so it can’t be changed
        here. Update the care team from the patient’s profile.
      </div>
    )}
  </div>
);

export default PrimaryProviderAssignmentPanel;
